import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Link2, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';

const API = getApiBase();

/**
 * Safe slug editor with preview + availability check.
 * entityType: "individual" | "tree"
 */
export function ArchiveSlugEditor({
  entityType = 'individual',
  entityId,
  currentSlug = '',
  canEdit = false,
  onSaved,
}) {
  const [draft, setDraft] = useState(currentSlug || '');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState(null);
  const [previewPath, setPreviewPath] = useState('');

  useEffect(() => {
    setDraft(currentSlug || '');
    setAvailable(null);
  }, [currentSlug, entityId]);

  const basePath = entityType === 'tree' ? '/family' : '/person';

  const checkSlug = useCallback(async () => {
    if (!entityId || !draft.trim()) return;
    setChecking(true);
    try {
      const { data } = await axios.get(
        `${API}/${entityType === 'tree' ? 'trees' : 'individuals'}/${entityId}/slug/check`,
        { params: { slug: draft.trim() } }
      );
      setAvailable(data.available);
      setPreviewPath(data.preview_path || `${basePath}/${data.slug}`);
      if (data.slug && data.slug !== draft) setDraft(data.slug);
    } catch (e) {
      setAvailable(false);
      toast.error(e.response?.data?.detail || 'Could not check URL');
    } finally {
      setChecking(false);
    }
  }, [entityId, draft, entityType, basePath]);

  const saveSlug = async () => {
    if (!entityId || !draft.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/${entityType === 'tree' ? 'trees' : 'individuals'}/${entityId}/slug`,
        { slug: draft.trim() }
      );
      toast.success('Archive URL updated');
      setPreviewPath(data.preview_path);
      setAvailable(true);
      onSaved?.(data.slug);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save URL');
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit && !currentSlug) return null;

  return (
    <div className="space-y-3 rounded-xl border border-border/50 bg-muted/10 p-4" data-testid="archive-slug-editor">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-[#8B7355]" aria-hidden />
        <Label className="text-sm font-medium">Readable archive URL</Label>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        A clean link for this profile. System IDs stay private in the background.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="shrink-0 text-xs text-muted-foreground">{basePath}/</span>
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setAvailable(null);
          }}
          onBlur={checkSlug}
          disabled={!canEdit}
          placeholder="e.g. margaret-smith"
          className="h-9 font-mono text-sm"
          data-testid="slug-input"
        />
        {canEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 rounded-full"
            disabled={checking || !draft.trim()}
            onClick={checkSlug}
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
          </Button>
        ) : null}
      </div>
      {previewPath ? (
        <p className="text-xs text-muted-foreground">
          Preview: <span className="font-mono text-foreground/80">{previewPath}</span>
        </p>
      ) : null}
      {available === true ? (
        <p className="flex items-center gap-1 text-xs text-emerald-700">
          <Check className="h-3.5 w-3.5" aria-hidden /> Available
        </p>
      ) : null}
      {available === false ? (
        <p className="flex items-center gap-1 text-xs text-red-600">
          <X className="h-3.5 w-3.5" aria-hidden /> Already in use — try another
        </p>
      ) : null}
      {canEdit ? (
        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            className="rounded-full"
            disabled={saving || !draft.trim() || available === false}
            onClick={saveSlug}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save URL
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default ArchiveSlugEditor;
